"use client";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";

const BLOG_ARTICLE_BINDINGS = {
  "component.options.articleType": "state.article.articleType",
  "component.options.articleId": "state.article.articleId",
  "component.options.title": "state.article.title",
  "component.options.subtitle": "state.article.subtitle",
  "component.options.topicName": "state.article.subtitle",
  "component.options.readTime": "state.article.readTime",
  "component.options.creationDate": "state.article.creationDate",
  "component.options.author": "state.article.author",
  "component.options.showAuthor": "state.article.showAuthor",
  "component.options.image.altText": "state.article.image.altText",
  "component.options.image.smallReference": "state.article.image.smallReference",
  "component.options.image.mediumReference": "state.article.image.mediumReference",
  "component.options.image.largeReference": "state.article.image.largeReference",
  "component.options.mainImage.smallReference": "state.article.image.smallReference",
  "component.options.mainImage.mediumReference": "state.article.image.mediumReference",
  "component.options.mainImage.largeReference": "state.article.image.largeReference",
} as const;

export const blogArticleInputs = [
  {
    name: "title",
    type: "string",
    required: true,
    helperText: "The title of the blog post",
  },
  {
    name: "subtitle",
    type: "string",
    required: true,
    helperText: "The topic/category of the blog post",
  },
  {
    name: "readTime",
    type: "string",
    helperText: "Read time",
  },
  {
    name: "articleType",
    type: "string",
    enum: ["article", "video"],
    defaultValue: "article",
  },
  {
    name: "articleId",
    type: "string",
  },
  {
    name: "image",
    type: "object",
    subFields: [
      {
        name: "smallReference",
        type: "file",
        required: true,
        helperText: "Small version of the image",
        defaultValue: "",
      },
      {
        name: "mediumReference",
        type: "file",
        required: true,
        helperText: "Medium version of the image",
        defaultValue: "",
      },
      {
        name: "largeReference",
        type: "file",
        required: true,
        helperText: "Large version of the image",
      },
      {
        name: "altText",
        type: "string",
        required: true,
        helperText: "Alternative text for the image",
      },
    ],
  },
  {
    name: "creationDate",
    type: "date",
    required: true,
    helperText: "The creation date of the blog post",
  },
  {
    name: "showAuthor",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "author",
    type: "string",
    showIf: `options.get("showAuthor")`,
  },
  {
    name: "blogTags",
    type: "list",
    subFields: [
      {
        name: "tag",
        type: "string",
        helperText: "Blog tag",
      },
    ],
  },
  {
    name: "relatedArticlesTitle",
    type: "string",
    helperText: "The title of the blog related Articles",
    defaultValue: "Related Articles",
  },
  {
    name: "relatedArticlesCtaLabel",
    type: "string",
    helperText: "The label of the cta",
    defaultValue: "View all products",
  },
  {
    name: "relatedArticlesCtaUrl",
    type: "string",
    helperText: "The url of the cta",
    defaultValue: "/products",
  },
  {
    name: "relatedArticles",
    type: "list",
    subFields: [
      {
        name: "article",
        type: "reference",
        model: "blog-single-article-section-model",
      },
    ],
  },
] as const satisfies Inputs;

export const BlogArticleComponent: RegisteredComponent = {
  name: "Blog Article",
  component: dynamic(async () =>
    withValidation(blogArticleInputs, (await import("./BlogArticle")).BlogArticle, "BlogArticle")
  ),
  canHaveChildren: true,
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
    builderComponents: true,
    builderContext: true,
  },
  inputs: blogArticleInputs,
  defaults: {
    bindings: BLOG_ARTICLE_BINDINGS,
  },
};
